import React, { useState, useEffect, useCallback } from 'react';
import { presupuestosAPI, platosAPI } from '../api/api';
import { Plus, Trash2, Edit2, Wine, AlertTriangle, Check, X } from 'lucide-react';

const BebidasAsignadas = ({ 
  presupuestoId, 
  presupuestoNumero,
  menusConBebida = [],
  bebidasActuales = [],
  bebidasConfirmadas = false,
  onUpdate,
  inTab = false,
  // U-98: valor que cambia cuando el presupuesto cambia fuera de este bloque
  // (p. ej. su `rev`): las bebidas se vuelven a cargar
  recargar = null
}) => {
  const [bebidas, setBebidas] = useState(bebidasActuales || []);
  const [platosBebida, setPlatosBebida] = useState([]);
  const [menusInfo, setMenusInfo] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBebida, setEditingBebida] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmadas, setConfirmadas] = useState(bebidasConfirmadas);
  const [error, setError] = useState('');
  // Multievento: aviso de que el importe de las bebidas no entra en el total y
  // jornadas para el selector de la bebida
  const [avisoImporte, setAvisoImporte] = useState(null);
  const [jornadas, setJornadas] = useState([]);
  
  const [formData, setFormData] = useState({
    menu_id: '',
    jornada_id: '',
    plato_id: '',
    cantidad_personas: 0,
    es_extra: false,
    suplemento_unitario: 0,
    precio_unitario: 0,
    descuento_porcentaje: 0,
    notas: ''
  });

  const loadData = useCallback(async () => {
    try {
      // Cargar platos tipo bebida
      const platosRes = await platosAPI.getBebidas();
      setPlatosBebida(platosRes.data || []);
      
      // Cargar bebidas del presupuesto
      if (presupuestoId) {
        const bebidasRes = await presupuestosAPI.getBebidas(presupuestoId);
        setBebidas(bebidasRes.data?.bebidas || []);
        setMenusInfo(bebidasRes.data?.menus_con_bebida || []);
        setConfirmadas(bebidasRes.data?.bebidas_confirmadas || false);
        setAvisoImporte(bebidasRes.data?.aviso_importe_bebidas || null);
        setJornadas(bebidasRes.data?.jornadas || []);
      }
    } catch (err) {
      console.error('Error cargando datos de bebidas:', err);
    }
  }, [presupuestoId]);

  // U-98: al montar (también al volver a abrir la pestaña), al cambiar de
  // presupuesto y cuando el presupuesto cambia fuera de este bloque
  useEffect(() => {
    loadData();
  }, [loadData, recargar]);

  // Calcular resumen de bebidas por menú
  const getResumenPorMenu = useCallback((menuId) => {
    const menuInfo = menusInfo.find(m => m.menu_id === menuId);
    if (!menuInfo) return null;
    
    const bebidasMenu = bebidas.filter(b => b.menu_id === menuId && !b.es_extra);
    const totalAsignado = bebidasMenu.reduce((sum, b) => sum + (b.cantidad_personas || 0), 0);
    const totalNecesario = menuInfo.total_bebidas_incluidas;
    
    return {
      ...menuInfo,
      totalAsignado,
      totalNecesario,
      completo: totalAsignado >= totalNecesario,
      faltan: Math.max(0, totalNecesario - totalAsignado)
    };
  }, [bebidas, menusInfo]);

  // Abrir modal para nueva bebida
  const handleAddBebida = (menuId = '', esExtra = false) => {
    const menuInfo = menusInfo.find(m => m.menu_id === menuId);
    setFormData({
      menu_id: menuId,
      jornada_id: '',
      plato_id: '',
      cantidad_personas: menuInfo ? menuInfo.total_bebidas_incluidas - (getResumenPorMenu(menuId)?.totalAsignado || 0) : 0,
      es_extra: esExtra,
      suplemento_unitario: 0,
      notas: ''
    });
    setEditingBebida(null);
    setShowModal(true);
  };

  // Abrir modal para editar
  const handleEditBebida = (bebida) => {
    setFormData({
      menu_id: bebida.menu_id,
      jornada_id: bebida.jornada_id || '',
      plato_id: bebida.plato_id,
      cantidad_personas: bebida.cantidad_personas,
      es_extra: bebida.es_extra,
      suplemento_unitario: bebida.suplemento_unitario || 0,
      precio_unitario: bebida.precio_unitario || 0,
      descuento_porcentaje: bebida.descuento_porcentaje || 0,
      notas: bebida.notas || ''
    });
    setEditingBebida(bebida);
    setShowModal(true);
  };

  // Guardar bebida
  const handleSave = async () => {
    if (!formData.plato_id || formData.cantidad_personas <= 0) {
      setError('Selecciona una bebida y especifica la cantidad');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      let response;
      // La jornada solo se envía en un multievento (vacía = de todo el evento)
      const { jornada_id: jornadaId, ...resto } = formData;
      const payload = jornadas.length > 0 ? { ...resto, jornada_id: jornadaId || null } : resto;
      if (editingBebida) {
        response = await presupuestosAPI.updateBebida(presupuestoId, editingBebida.id, payload);
      } else {
        response = await presupuestosAPI.addBebida(presupuestoId, payload);
      }
      
      // Mostrar warnings si los hay
      if (response.data?.warnings && response.data.warnings.length > 0) {
        alert(response.data.warnings.join('\n'));
      }
      
      await loadData();
      setShowModal(false);
      
      if (onUpdate) {
        const bebidasRes = await presupuestosAPI.getBebidas(presupuestoId);
        // Incluir el nuevo importe total en el callback
        onUpdate({
          ...bebidasRes.data,
          nuevo_importe_total: response.data?.nuevo_importe_total
        });
      }
    } catch (err) {
      console.error('Error guardando bebida:', err);
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setIsLoading(false);
    }
  };

  // Eliminar bebida
  const handleDelete = async (bebidaId) => {
    if (!window.confirm('¿Eliminar esta bebida?')) return;
    
    try {
      const response = await presupuestosAPI.deleteBebida(presupuestoId, bebidaId);
      await loadData();
      
      if (onUpdate) {
        const bebidasRes = await presupuestosAPI.getBebidas(presupuestoId);
        onUpdate({
          ...bebidasRes.data,
          nuevo_importe_total: response.data?.nuevo_importe_total,
          bebidas_confirmadas: response.data?.bebidas_confirmadas
        });
      }
    } catch (err) {
      console.error('Error eliminando bebida:', err);
    }
  };

  // Confirmar bebidas
  const handleConfirmar = async () => {
    setIsLoading(true);
    try {
      await presupuestosAPI.confirmarBebidas(presupuestoId);
      setConfirmadas(true);
      
      if (onUpdate) {
        const bebidasRes = await presupuestosAPI.getBebidas(presupuestoId);
        onUpdate(bebidasRes.data);
      }
    } catch (err) {
      console.error('Error confirmando bebidas:', err);
      alert(err.response?.data?.detail || 'Error al confirmar bebidas');
    } finally {
      setIsLoading(false);
    }
  };

  // Desconfirmar bebidas (para permitir edición)
  const handleDesconfirmar = async () => {
    if (!window.confirm('¿Desconfirmar las bebidas para poder editarlas?')) return;
    
    setIsLoading(true);
    try {
      // Actualizar el presupuesto para desconfirmar bebidas
      // `_origen: 'bebidas'`: lo manda la pestaña que edita las bebidas (el
      // servidor conserva estos campos frente a copias viejas de la ficha)
      await presupuestosAPI.update(presupuestoId, { bebidas_confirmadas: false, _origen: 'bebidas' });
      setConfirmadas(false);
      
      if (onUpdate) {
        const bebidasRes = await presupuestosAPI.getBebidas(presupuestoId);
        onUpdate({ ...bebidasRes.data, bebidas_confirmadas: false });
      }
    } catch (err) {
      console.error('Error desconfirmando bebidas:', err);
      alert('Error al desconfirmar bebidas');
    } finally {
      setIsLoading(false);
    }
  };

  // Obtener plato seleccionado para mostrar info
  const getPlatoInfo = (platoId) => {
    return platosBebida.find(p => p.id === platoId);
  };

  // Jornada de una bebida (multievento): la suya o "Todo el evento" (se reparte)
  const textoJornada = (bebida) => {
    if (jornadas.length === 0) return null;
    const j = jornadas.find(x => x.id === bebida.jornada_id);
    return j ? j.etiqueta : 'Todo el evento (se reparte por personas)';
  };

  // Calcular unidades necesarias
  const calcularUnidades = (cantidadPersonas, porciones) => {
    return Math.ceil(cantidadPersonas / (porciones || 1));
  };

  // Si no hay menús con bebida, no mostrar nada
  if (menusInfo.length === 0 && bebidas.length === 0) {
    return null;
  }

  // Separar bebidas incluidas y extras
  const bebidasIncluidas = bebidas.filter(b => !b.es_extra);
  const bebidasExtra = bebidas.filter(b => b.es_extra);
  
  // Calcular totales
  const totalSuplementos = bebidasIncluidas.reduce((sum, b) => sum + (b.total || 0), 0);
  const totalExtra = bebidasExtra.reduce((sum, b) => sum + (b.total || 0), 0);
  
  // Verificar si todas las bebidas incluidas están completas
  const todasCompletas = menusInfo.every(menu => {
    const resumen = getResumenPorMenu(menu.menu_id);
    return resumen?.completo;
  });

  return (
    <div className={inTab ? '' : 'bg-white rounded-lg shadow-sm border border-gray-200 p-5'}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Wine className="w-5 h-5 text-purple-600" />
          <h3 className={`${inTab ? 'text-base' : 'text-lg'} font-bold text-gray-900`}>Bebidas</h3>
          {confirmadas ? (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" /> Confirmadas
            </span>
          ) : !todasCompletas && menusInfo.length > 0 ? (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Pendiente
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {confirmadas && (
            <button
              onClick={handleDesconfirmar}
              className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 flex items-center gap-1"
              title="Desconfirmar para poder editar"
            >
              <Edit2 className="w-3 h-3" />
              Editar
            </button>
          )}
          <button
            onClick={() => handleAddBebida('', false)}
            className="px-3 py-1 bg-purple-500 text-white text-xs rounded-lg hover:bg-purple-600 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Añadir
          </button>
        </div>
      </div>

      {/* Resumen por menú */}
      {menusInfo.length > 0 && (
        <div className="space-y-2 mb-4">
          {/* Resumen total de todos los menús */}
          {menusInfo.length > 1 && (
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 mb-2">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-sm font-medium text-purple-900">📊 Total bebidas incluidas (todos los menús)</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-purple-700">
                    {menusInfo.reduce((sum, m) => sum + (getResumenPorMenu(m.menu_id)?.totalAsignado || 0), 0)}/
                    {menusInfo.reduce((sum, m) => sum + (m.total_bebidas_incluidas || 0), 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Detalle por menú */}
          {menusInfo.map(menu => {
            const resumen = getResumenPorMenu(menu.menu_id);
            if (!resumen) return null;
            
            return (
              <div 
                key={menu.menu_id}
                className={`p-3 rounded-lg border ${
                  resumen.completo 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{menu.menu_nombre}</span>
                    <p className="text-xs text-gray-600">
                      {menu.num_personas} pers × {menu.bebidas_por_persona} bebida(s) = {resumen.totalNecesario} incluidas
                    </p>
                    {(menu.personas_por_jornada || []).length > 1 && (
                      <p className="text-xs text-gray-500">
                        {menu.personas_por_jornada.map(x => `${x.etiqueta}: ${x.num_personas}`).join(' · ')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`text-sm font-bold ${resumen.completo ? 'text-green-600' : 'text-yellow-600'}`}>
                      {resumen.totalAsignado}/{resumen.totalNecesario}
                    </span>
                    {!resumen.completo && (
                      <button
                        onClick={() => handleAddBebida(menu.menu_id, false)}
                        className="ml-2 text-xs text-purple-600 hover:underline"
                      >
                        + Añadir
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista de bebidas incluidas */}
      {bebidasIncluidas.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Bebidas Incluidas</h4>
          <div className="space-y-2">
            {bebidasIncluidas.map(bebida => {
              const plato = getPlatoInfo(bebida.plato_id);
              return (
                <div 
                  key={bebida.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{bebida.plato_nombre || plato?.nombre}</span>
                      {bebida.porciones_por_unidad > 1 && (
                        <span className="text-xs text-gray-500">
                          ({bebida.porciones_por_unidad} porc/ud)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {bebida.cantidad_personas} pers → {bebida.unidades_necesarias} uds
                      {textoJornada(bebida) && <span className="text-gray-500 ml-2">{textoJornada(bebida)}</span>}
                      {bebida.suplemento_unitario > 0 && (
                        <span className="text-purple-600 ml-2">
                          +{bebida.suplemento_unitario.toFixed(2)}€/ud
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {bebida.total > 0 && (
                      <span className="text-sm font-semibold text-purple-600">
                        +{bebida.total.toFixed(2)}€
                      </span>
                    )}
                    <button
                      onClick={() => handleEditBebida(bebida)}
                      className="p-1 text-gray-400 hover:text-blue-500"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(bebida.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bebidas extra */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold text-gray-700">Bebidas Extra (Tarificadas)</h4>
          <button
            onClick={() => handleAddBebida('', true)}
            className="text-xs text-purple-600 hover:underline"
          >
            + Añadir extra
          </button>
        </div>
        {bebidasExtra.length > 0 ? (
          <div className="space-y-2">
            {bebidasExtra.map(bebida => {
              const plato = getPlatoInfo(bebida.plato_id);
              const tieneDescuento = bebida.descuento_porcentaje > 0;
              const esGratis = bebida.descuento_porcentaje === 100;
              return (
                <div 
                  key={bebida.id}
                  className={`flex items-center justify-between p-2 rounded border ${esGratis ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{bebida.plato_nombre || plato?.nombre}</span>
                      {esGratis ? (
                        <span className="text-xs bg-green-200 text-green-700 px-1 rounded">🎁 CORTESÍA</span>
                      ) : tieneDescuento ? (
                        <span className="text-xs bg-yellow-200 text-yellow-700 px-1 rounded">-{bebida.descuento_porcentaje}%</span>
                      ) : (
                        <span className="text-xs bg-blue-200 text-blue-700 px-1 rounded">EXTRA</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {bebida.cantidad_personas} pers → {bebida.unidades_necesarias} uds × {bebida.precio_unitario?.toFixed(2)}€
                      {tieneDescuento && !esGratis && (
                        <span className="text-green-600"> ({bebida.descuento_porcentaje}% dto)</span>
                      )}
                      {textoJornada(bebida) && <span className="text-gray-500 ml-2">{textoJornada(bebida)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${esGratis ? 'text-green-600' : 'text-blue-600'}`}>
                      {esGratis ? '0.00€' : `+${bebida.total.toFixed(2)}€`}
                    </span>
                    <button
                      onClick={() => handleEditBebida(bebida)}
                      className="p-1 text-gray-400 hover:text-blue-500"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(bebida.id)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No hay bebidas extra</p>
        )}
      </div>

      {/* Totales */}
      {(totalSuplementos > 0 || totalExtra > 0) && (
        <div className="border-t border-gray-200 pt-3 space-y-1">
          {totalSuplementos > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Suplemento bebidas premium:</span>
              <span className="font-semibold text-purple-600">+{totalSuplementos.toFixed(2)}€</span>
            </div>
          )}
          {totalExtra > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total bebidas extra:</span>
              <span className="font-semibold text-blue-600">+{totalExtra.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold border-t pt-2">
            <span>Total bebidas:</span>
            <span className="text-green-600">+{(totalSuplementos + totalExtra).toFixed(2)}€</span>
          </div>
          {avisoImporte && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="aviso-importe-bebidas">
              {avisoImporte}
            </p>
          )}
        </div>
      )}

      {/* Botón confirmar */}
      {!confirmadas && todasCompletas && bebidas.length > 0 && (
        <button
          onClick={handleConfirmar}
          disabled={isLoading}
          className="mt-4 w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" />
          Confirmar Bebidas
        </button>
      )}

      {/* Modal añadir/editar bebida */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">
                {editingBebida ? 'Editar Bebida' : (formData.es_extra ? 'Añadir Bebida Extra' : 'Añadir Bebida Incluida')}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Selector de menú (solo para bebidas incluidas) */}
              {!formData.es_extra && menusInfo.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Menú</label>
                  <select
                    value={formData.menu_id}
                    onChange={(e) => setFormData({ ...formData, menu_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">-- Seleccionar menú --</option>
                    {menusInfo.map(menu => (
                      <option key={menu.menu_id} value={menu.menu_id}>
                        {menu.menu_nombre} ({menu.total_bebidas_incluidas} incluidas)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selector de bebida */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bebida</label>
                <select
                  value={formData.plato_id}
                  onChange={(e) => setFormData({ ...formData, plato_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">-- Seleccionar bebida --</option>
                  {[...platosBebida]
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map(plato => (
                    <option key={plato.id} value={plato.id}>
                      {plato.nombre} {plato.porciones > 1 ? `(${plato.porciones} porc/ud)` : ''} - Coste: {(plato.coste_total || plato.coste_ingredientes || 0).toFixed(2)}€
                    </option>
                  ))}
                </select>
              </div>

              {/* Info del plato seleccionado */}
              {formData.plato_id && (
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  {(() => {
                    const plato = getPlatoInfo(formData.plato_id);
                    if (!plato) return null;
                    const unidades = calcularUnidades(formData.cantidad_personas, plato.porciones);
                    const precioBase = plato.precio_sugerido || 0;
                    const descuento = formData.descuento_porcentaje || 0;
                    const precioConDescuento = precioBase * (1 - descuento / 100);
                    const totalConDescuento = unidades * precioConDescuento;
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Porciones por unidad:</span>
                          <span className="font-medium">{plato.porciones || 1}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Unidades necesarias:</span>
                          <span className="font-medium">{unidades}</span>
                        </div>
                        {formData.es_extra && (
                          <>
                            <div className="flex justify-between mt-1 pt-1 border-t">
                              <span className="text-gray-600">Precio unitario:</span>
                              <span className="font-medium">{precioBase.toFixed(2)}€</span>
                            </div>
                            {descuento > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>Descuento ({descuento}%):</span>
                                <span>-{(unidades * precioBase * descuento / 100).toFixed(2)}€</span>
                              </div>
                            )}
                            <div className="flex justify-between mt-1 pt-1 border-t">
                              <span className="text-gray-600">Total a cobrar:</span>
                              <span className={`font-bold ${descuento === 100 ? 'text-green-600' : 'text-blue-600'}`}>
                                {descuento === 100 ? '🎁 GRATIS' : `${totalConDescuento.toFixed(2)}€`}
                              </span>
                            </div>
                          </>
                        )}
                        {!formData.es_extra && formData.suplemento_unitario > 0 && (
                          <div className="flex justify-between mt-1 pt-1 border-t">
                            <span className="text-gray-600">Suplemento total:</span>
                            <span className="font-bold text-purple-600">
                              {(unidades * formData.suplemento_unitario).toFixed(2)}€
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Jornada (solo multievento): vacía = todo el evento, se reparte por personas */}
              {jornadas.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jornada</label>
                  <select
                    data-testid="bebida-jornada"
                    value={formData.jornada_id || ''}
                    onChange={(e) => setFormData({ ...formData, jornada_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Todo el evento (se reparte por personas)</option>
                    {jornadas.map(j => (
                      <option key={j.id} value={j.id}>{j.etiqueta}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Cantidad de personas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personas que la quieren
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.cantidad_personas}
                  onChange={(e) => setFormData({ ...formData, cantidad_personas: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              {/* Suplemento (solo para bebidas incluidas premium) */}
              {!formData.es_extra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Suplemento por unidad (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.suplemento_unitario}
                    onChange={(e) => setFormData({ ...formData, suplemento_unitario: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="0.00 para bebidas estándar"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Añade un suplemento si es una bebida premium (ej: vino en lugar de refresco)
                  </p>
                </div>
              )}

              {/* Descuento (solo para bebidas extra) */}
              {formData.es_extra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descuento (%)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={formData.descuento_porcentaje || 0}
                      onChange={(e) => setFormData({ ...formData, descuento_porcentaje: parseFloat(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="0"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, descuento_porcentaje: 100 })}
                      className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm whitespace-nowrap"
                    >
                      🎁 Regalar
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    100% = bebida gratis / cortesía de la casa
                  </p>
                </div>
              )}

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                <input
                  type="text"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ej: Sin azúcar, con hielo..."
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
              >
                {isLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BebidasAsignadas;
